import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceDataType,
  GridSpaceType,
  LevelType
} from 'data/types';
import { useCallback, useMemo, useRef } from 'react';
import { Vector3 } from 'three';
import { SCALE } from 'utils/3d';
import { getEquipment3DPlacement } from 'utils/3d-equipment';
import {
  moveAirHandlerToGridPosition,
  moveCondenserToGridPosition
} from 'utils/equipment-move';
import { beginLinesetDrag, endLinesetDrag } from 'utils/lineset-drag-state';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import TranslateGizmo3D from './translate-gizmo-3d';

export type EquipmentDragOverride = {
  objectId: string;
  x: number;
  y: number;
};

type EquipmentGizmo3DProps = {
  object: GridObjectType;
  spaces: GridSpaceType[];
  footprints: FootprintPolygonsType;
  structure: LevelType[];
  levelIndex: number;
  yOffset: number;
  configurationId: string;
  gridSpaces: GridSpaceDataType[];
  onDragOverrideChange: (override: EquipmentDragOverride | null) => void;
  onInteractionChange?: (state: TransformControlsInteractionState) => void;
};

export default function EquipmentGizmo3D({
  object,
  spaces,
  footprints,
  structure,
  levelIndex,
  yOffset,
  configurationId,
  gridSpaces,
  onDragOverrideChange,
  onInteractionChange
}: EquipmentGizmo3DProps) {
  const dispatch = useAppDispatch();
  const { configurations } = useAppSelector((state) => state.configurations);
  const configurationsRef = useRef(configurations);
  const objectRef = useRef(object);
  const dragStartGridRef = useRef({ x: 0, y: 0 });
  const dragStartAnchorRef = useRef({ x: 0, z: 0 });
  const lastGridRef = useRef({ x: 0, y: 0 });

  configurationsRef.current = configurations;
  objectRef.current = object;

  const placement = useMemo(
    () => getEquipment3DPlacement(object, spaces, footprints),
    [object, spaces, footprints]
  );
  const placementRef = useRef(placement);
  placementRef.current = placement;

  const dragId = `equipment-3d-${object.id}`;

  const handleDragStart = useCallback(() => {
    const startGrid = {
      x: Math.round(objectRef.current.position.x),
      y: Math.round(objectRef.current.position.y)
    };
    dragStartGridRef.current = startGrid;
    lastGridRef.current = startGrid;
    dragStartAnchorRef.current = {
      x: placementRef.current.x,
      z: placementRef.current.z
    };
    beginLinesetDrag(dragId);
  }, [dragId]);

  const handleDragChange = useCallback(
    (anchorPosition: Vector3) => {
      const deltaGridX = Math.round(
        (anchorPosition.x - dragStartAnchorRef.current.x) / SCALE
      );
      const deltaGridY = Math.round(
        (anchorPosition.z - dragStartAnchorRef.current.z) / SCALE
      );
      const gridX = Math.max(0, dragStartGridRef.current.x + deltaGridX);
      const gridY = Math.max(0, dragStartGridRef.current.y + deltaGridY);
      lastGridRef.current = { x: gridX, y: gridY };
      onDragOverrideChange({
        objectId: objectRef.current.id,
        x: gridX,
        y: gridY
      });

      const preview = getEquipment3DPlacement(
        {
          ...objectRef.current,
          position: { ...objectRef.current.position, x: gridX, y: gridY }
        },
        spaces,
        footprints
      );
      anchorPosition.set(preview.x, preview.y + yOffset, preview.z);
    },
    [spaces, footprints, yOffset, onDragOverrideChange]
  );

  const handleDragEnd = useCallback(
    (anchorPosition: Vector3 | null) => {
      const startGrid = dragStartGridRef.current;
      const finalGrid = lastGridRef.current;
      const moved =
        anchorPosition !== null &&
        (finalGrid.x !== startGrid.x || finalGrid.y !== startGrid.y);

      if (moved) {
        const currentObject = objectRef.current;
        if (currentObject.type === 'condenser') {
          moveCondenserToGridPosition({
            dispatch,
            configurations: configurationsRef.current,
            configurationUniqueId: configurationId,
            condenserUniqueId: currentObject.itemId,
            x: finalGrid.x,
            y: finalGrid.y,
            levels: structure,
            levelIndex,
            gridSpaces
          });
        } else {
          moveAirHandlerToGridPosition({
            dispatch,
            configurations: configurationsRef.current,
            configurationUniqueId: configurationId,
            zoneItemId: currentObject.id.replace('-air-handler', ''),
            x: finalGrid.x,
            y: finalGrid.y,
            gridSpaces
          });
        }
      }

      onDragOverrideChange(null);
      setTimeout(() => {
        endLinesetDrag(dragId);
      }, 0);
    },
    [
      dispatch,
      configurationId,
      structure,
      levelIndex,
      gridSpaces,
      onDragOverrideChange,
      dragId
    ]
  );

  return (
    <TranslateGizmo3D
      position={[placement.x, placement.y + yOffset, placement.z]}
      showY={false}
      onDragStart={handleDragStart}
      onDragChange={handleDragChange}
      onDragEnd={handleDragEnd}
      onInteractionChange={onInteractionChange}
    />
  );
}
